import refreshToken from './refreshToken'
import withCrossTabLock from './withCrossTabLock'


/**
 * Issues token refreshes, collapsing concurrent callers onto one request and
 * holding open tabs to one at a time.
 *
 * A page's queries tend to fail together when a token expires, since they
 * were all issued with the same credential. Left uncollapsed, each failure
 * would start its own refresh, and because the server rotates the refresh
 * cookie on every call, overlapping refreshes race to invalidate each
 * other's credential.
 *
 * That cookie belongs to every tab, so the same race runs between them, and
 * collapsing within one tab does nothing about it. Tabs restored together
 * hold tokens of near-identical age and so come due together; the server
 * rotates the cookie for whichever arrives first, leaving the rest spending a
 * credential that no longer exists. Waiting turns that into each tab
 * refreshing in turn, with the cookie its predecessor left behind.
 */
export default class TokenRefresher {
  constructor() {
    this._inFlight = null
  }

  /**
   * Refreshes the access token and stores the result. Callers arriving while
   * a refresh is already running share its outcome instead of starting a
   * second one.
   *
   * @returns {Promise} resolves once the replacement token is stored,
   *   rejects with the underlying Axios error if the refresh failed
   */
  refresh() {
    if ( this._inFlight ) {
      return this._inFlight
    }

    this._inFlight = withCrossTabLock(refreshToken)
      .finally(() => {
        this._inFlight = null
      })

    return this._inFlight
  }
}
